<template>
  <div class="project-radar-chart">
    <div v-if="!hasData" class="no-data-tip">暂无数据</div>
    <div v-else id="projectRadarChart" ref="chartRef" class="chart-content"></div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, watch, nextTick, onUnmounted } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  }
});

const chartRef = ref(null);
let chartInstance = null;

// Mock数据，确保组件在没有数据时也能正常显示
const mockProjectData = {
  categories: ['50米跑', '1000米跑', '立定跳远', '引体向上', '肺活量'],
  series: [
    {
      name: '当前成绩',
      data: [85, 70, 88, 60, 82]
    },
    {
      name: '班级平均',
      data: [78, 75, 80, 65, 80]
    }
  ]
};

// 检查是否有有效数据
const hasData = ref(true);

// 初始化图表
const initChart = () => {
  // 即使没有DOM引用也创建图表实例，确保在DOM可用时能立即渲染
  if (!chartRef.value) {
    // 设置一个短暂的延时后重试
    setTimeout(() => {
      if (chartRef.value) {
        initChart();
      }
    }, 100);
    return;
  }
  
  // 确保有数据
  const chartData = props.data && Object.keys(props.data).length > 0 ? props.data : mockProjectData;
  hasData.value = chartData.categories && chartData.categories.length > 0 &&
                 chartData.series && chartData.series.length > 0 &&
                 chartData.series[0].data && chartData.series[0].data.length > 0;
  
  if (!hasData.value) return;
  
  // 销毁已存在的图表实例
  if (chartInstance) {
    chartInstance.dispose();
  }
  
  try {
    chartInstance = echarts.init(chartRef.value);
    
    // 提取类别和系列数据
    const categories = chartData.categories;
    const series = chartData.series;
    
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return `${params.seriesName}: ${params.value}分`;
        }
      },
      legend: {
        data: series.map(item => item.name),
        bottom: 0
      },
      radar: {
        indicator: categories.map(item => ({
          name: item,
          max: 100
        })),
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(240, 240, 240, 0.3)']
          }
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(120, 120, 120, 0.5)'
          }
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(120, 120, 120, 0.5)'
          }
        }
      },
      series: [
        {
          name: '项目能力分布',
          type: 'radar',
          data: series.map((item, index) => ({
            value: item.data,
            name: item.name,
            areaStyle: {
              opacity: 0.3
            },
            lineStyle: {
              width: 2
            },
            symbolSize: 6
          })),
          color: ['#1890ff', '#faad14', '#52c41a', '#ff4d4f']
        }
      ]
    };
    
    chartInstance.setOption(option);
    
    // 强制重绘一次
    chartInstance.resize();
  } catch (error) {
    console.error('初始化项目能力雷达图失败:', error);
  }
};

// 主动触发图表重绘的方法，供父组件调用
const triggerRedraw = () => {
  if (chartInstance) {
    chartInstance.resize();
  } else {
    // 如果实例不存在，重新初始化
    initChart();
  }
};

// 暴露方法给父组件
defineExpose({
  triggerRedraw
});

// 处理窗口大小变化
const handleResize = () => {
  if (chartInstance) {
    chartInstance.resize();
  }
};

// 监听数据变化
watch(
  () => props.data,
  () => {
    nextTick(() => {
      initChart();
    });
  },
  { deep: true }
);

onMounted(() => {
  nextTick(() => {
    initChart();
    window.addEventListener('resize', handleResize);
  });
});

// 组件卸载时清理资源
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});
</script>

<style scoped>
.project-radar-chart {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.chart-content {
  width: 100%;
  height: 100%;
  min-height: 300px;
}

.no-data-tip {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #999;
  font-size: 14px;
  background-color: #fafafa;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .project-radar-chart {
    height: 300px;
  }
}
</style>